import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box } from '@mui/material';

// Convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Apply watercolor effect to a color
const applyWatercolorEffect = (ctx, x, y, cellWidth, cellHeight, color) => {
  const gradient = ctx.createRadialGradient(
    x + cellWidth * 0.3, y + cellHeight * 0.3, 0,
    x + cellWidth * 0.5, y + cellHeight * 0.5, Math.max(cellWidth, cellHeight) * 0.8
  );

  // Create a lighter center and darker edges for watercolor effect
  const rgb = hexToRgb(color);
  if (rgb) {
    const lightColor = `rgba(${Math.min(255, rgb.r + 30)}, ${Math.min(255, rgb.g + 30)}, ${Math.min(255, rgb.b + 30)}, 0.9)`;
    const darkColor = `rgba(${Math.max(0, rgb.r - 20)}, ${Math.max(0, rgb.g - 20)}, ${Math.max(0, rgb.b - 20)}, 0.95)`;

    gradient.addColorStop(0, lightColor);
    gradient.addColorStop(0.7, color);
    gradient.addColorStop(1, darkColor);
  } else {
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, color);
  }

  return gradient;
};

// Apply soft effect to a color
const applySoftEffect = (ctx, x, y, cellWidth, cellHeight, color) => {
  const gradient = ctx.createLinearGradient(x, y, x + cellWidth, y + cellHeight);
  const rgb = hexToRgb(color);

  if (rgb) {
    const lightColor = `rgba(${Math.min(255, rgb.r + 15)}, ${Math.min(255, rgb.g + 15)}, ${Math.min(255, rgb.b + 15)}, 0.95)`;
    gradient.addColorStop(0, lightColor);
    gradient.addColorStop(1, color);
  } else {
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, color);
  }

  return gradient;
};

// Gradient effect: left (darker) to right (lighter)
const applyLeftToRightGradient = (ctx, x, y, cellWidth, cellHeight, color) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const gradient = ctx.createLinearGradient(x, y, x + cellWidth, y);
  const darkColor = `rgba(${Math.max(0, rgb.r - 30)}, ${Math.max(0, rgb.g - 30)}, ${Math.max(0, rgb.b - 30)}, 1)`;
  const lightColor = `rgba(${Math.min(255, rgb.r + 30)}, ${Math.min(255, rgb.g + 30)}, ${Math.min(255, rgb.b + 30)}, 1)`;
  gradient.addColorStop(0, darkColor);
  gradient.addColorStop(1, lightColor);
  return gradient;
};

// Sketch/Pencil style: cross-hatch lines
const applySketchEffect = (ctx, x, y, cellWidth, cellHeight, color) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.7;
  ctx.fillRect(x, y, cellWidth, cellHeight);
  ctx.globalAlpha = 0.25;
  ctx.strokeStyle = '#444';
  for (let i = 0; i < 6; i++) {
    const offset = (i / 6) * cellHeight;
    ctx.beginPath();
    ctx.moveTo(x, y + offset);
    ctx.lineTo(x + cellWidth, y + offset + (Math.random() - 0.5) * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x + offset, y);
    ctx.lineTo(x + offset + (Math.random() - 0.5) * 2, y + cellHeight);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
};

// Rough/Imperfect Edges
const drawRoughRect = (ctx, x, y, w, h, color) => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + Math.random() * 2, y + Math.random() * 2);
  ctx.lineTo(x + w + Math.random() * 2, y + Math.random() * 2);
  ctx.lineTo(x + w + Math.random() * 2, y + h + Math.random() * 2);
  ctx.lineTo(x + Math.random() * 2, y + h + Math.random() * 2);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
};

// Watercolor Bleed
const applyWatercolorBleed = (ctx, x, y, cellWidth, cellHeight, color) => {
  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.ellipse(x + cellWidth / 2, y + cellHeight / 2, cellWidth * 0.6, cellHeight * 0.6, 0, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.filter = 'blur(1.5px)';
  ctx.fill();
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
  ctx.restore();
};

// Crayon/Colored Pencil
const applyCrayonEffect = (ctx, x, y, cellWidth, cellHeight, color) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.85;
  ctx.fillRect(x, y, cellWidth, cellHeight);
  ctx.globalAlpha = 0.3;
  for (let i = 0; i < 18; i++) {
    ctx.strokeStyle = color;
    ctx.beginPath();
    const startX = x + Math.random() * cellWidth;
    const startY = y + Math.random() * cellHeight;
    ctx.moveTo(startX, startY);
    ctx.lineTo(startX + Math.random() * 6 - 3, startY + Math.random() * 6 - 3);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
};

// Ink Outline
const drawInkOutline = (ctx, x, y, cellWidth, cellHeight, color) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(x, y, cellWidth, cellHeight);
  ctx.strokeStyle = '#222';
  ctx.lineWidth = 0.4;
  ctx.beginPath();
  ctx.moveTo(x + Math.random() * 1.5, y + Math.random() * 1.5);
  ctx.lineTo(x + cellWidth + Math.random() * 1.5, y + Math.random() * 1.5);
  ctx.lineTo(x + cellWidth + Math.random() * 1.5, y + cellHeight + Math.random() * 1.5);
  ctx.lineTo(x + Math.random() * 1.5, y + cellHeight + Math.random() * 1.5);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
};

// Draw a fat V shape (knit stitch look, pointing up, no background)
const drawKnitStitch = (ctx, x, y, cellWidth, cellHeight, color) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(cellWidth, cellHeight) * 0.22;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  // V shape: from top left to center bottom to top right (flipped up)
  ctx.moveTo(x + cellWidth * 0.18, y + cellHeight * 0.3);
  ctx.lineTo(x + cellWidth * 0.5, y + cellHeight * 0.75);
  ctx.lineTo(x + cellWidth * 0.82, y + cellHeight * 0.3);
  ctx.stroke();
  ctx.restore();
};


// Draw a heart as a very thick V shape (reset heart style, slightly narrower)
const drawHeartShape = (ctx, x, y, cellWidth, cellHeight, color) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(cellWidth, cellHeight) * 0.55; // very thick
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  // V shape: from top left to center bottom to top right, slightly narrower
  const pad = cellWidth * 0.26; // horizontal padding
  ctx.moveTo(x + pad + Math.random() * 2, y + cellHeight * 0.40 + Math.random() * 2);
  ctx.lineTo(x + cellWidth / 2 + Math.random() * 2, y + cellHeight * 0.76 + Math.random() * 2);
  ctx.lineTo(x + cellWidth - pad + Math.random() * 2, y + cellHeight * 0.40 + Math.random() * 2);
  ctx.stroke();
  ctx.restore();
};

const ColorThumbnail = forwardRef(({
  colordata,
  colors,
  width = 120,
  height = 90,
  view = 'default', // 'default', 'watercolor', 'soft', 'minimal', 'leftToRightGradient'
  cellRatio = '4:3', // '1:1', '3:4', '4:3', '2:3', '3:2'
  borderRadius = 4,
  showBorder = true,
  showGrid = false // show/hide gridlines
}, ref) => {
  const canvasRef = useRef(null);

  // Calculate cell aspect ratio
  const getCellDimensions = (cellRatio) => {
    const ratios = {
      '1:1': { width: 1, height: 1 },
      '3:4': { width: 3, height: 4 },
      '4:3': { width: 4, height: 3 },
      '2:3': { width: 2, height: 3 },
      '3:2': { width: 3, height: 2 }
    };
    return ratios[cellRatio] || ratios['4:3'];
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !colordata || !colors) return;

    const ctx = canvas.getContext('2d');
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Set canvas size accounting for device pixel ratio
    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Scale context for crisp rendering
    ctx.scale(devicePixelRatio, devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (!colordata.length || !colordata[0].length) return;

    const rows = colordata.length;
    const cols = colordata[0].length;
    const cellDimensions = getCellDimensions(cellRatio);

    // Calculate cell size based on aspect ratio
    const cellWidth = width / cols;
    const cellHeight = height / rows;

    // Adjust cell dimensions to maintain aspect ratio
    const aspectRatio = cellDimensions.width / cellDimensions.height;
    let adjustedCellWidth = cellWidth;
    let adjustedCellHeight = cellHeight;

    if (cellWidth / cellHeight > aspectRatio) {
      adjustedCellWidth = cellHeight * aspectRatio;
    } else {
      adjustedCellHeight = cellWidth / aspectRatio;
    }

    // Center the grid
    const offsetX = (width - (adjustedCellWidth * cols)) / 2;
    const offsetY = (height - (adjustedCellHeight * rows)) / 2;

    // Draw each cell - always using top-down and left-to-right orientation
    for (let visualRow = 0; visualRow < rows; visualRow++) {
      for (let visualCol = 0; visualCol < cols; visualCol++) {
        // Since we always use top-down and left-to-right, visual coordinates = data coordinates
        const colorIndex = colordata[visualRow][visualCol];
        if (colorIndex === null || colorIndex === undefined) continue;
        const color = colors[colorIndex] || colors[0] || '#ffffff';
        const x = offsetX + visualCol * adjustedCellWidth;
        const y = offsetY + visualRow * adjustedCellHeight;
        // Apply different effects based on view
        switch (view) {
          case 'watercolor':
            ctx.fillStyle = applyWatercolorEffect(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            break;
          case 'soft':
            ctx.fillStyle = applySoftEffect(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            break;
          case 'leftToRightGradient':
            ctx.fillStyle = applyLeftToRightGradient(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            break;
          case 'sketch':
            applySketchEffect(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          case 'rough':
            drawRoughRect(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          case 'watercolorBleed':
            applyWatercolorBleed(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          case 'crayon':
            applyCrayonEffect(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          case 'ink':
            drawInkOutline(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          case 'minimal':
            ctx.fillStyle = color;
            break;
          case 'knitstitch':
            drawKnitStitch(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          case 'heart':
            drawHeartShape(ctx, x, y, adjustedCellWidth, adjustedCellHeight, color);
            continue;
          default:
            ctx.fillStyle = color;
        }
        if (view === 'minimal') {
          ctx.fillRect(x, y, adjustedCellWidth, adjustedCellHeight);
        } else {
          const radius = Math.min(adjustedCellWidth, adjustedCellHeight) * 0.1;
          ctx.beginPath();
          ctx.roundRect(x, y, adjustedCellWidth, adjustedCellHeight, radius);
          ctx.fill();
        }
        // Only draw cell borders if showGrid is true
        if (showGrid && view !== 'minimal' && adjustedCellWidth > 3 && adjustedCellHeight > 3) {
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    }
    // Draw gridlines if enabled
    if (showGrid) {
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 0.5;
      for (let r = 0; r <= rows; r++) {
        const y = offsetY + r * adjustedCellHeight;
        ctx.beginPath();
        ctx.moveTo(offsetX, y);
        ctx.lineTo(offsetX + cols * adjustedCellWidth, y);
        ctx.stroke();
      }
      for (let c = 0; c <= cols; c++) {
        const x = offsetX + c * adjustedCellWidth;
        ctx.beginPath();
        ctx.moveTo(x, offsetY);
        ctx.lineTo(x, offsetY + rows * adjustedCellHeight);
        ctx.stroke();
      }
      ctx.restore();
    }
  }, [colordata, colors, width, height, view, cellRatio, showGrid]);

  // Export as PNG method
  useImperativeHandle(ref, () => ({
    exportAsPng: (filename = 'color-chart-thumbnail', scale = 2) => {
      if (!colordata || !colors) return;
      const rows = colordata.length;
      const cols = colordata[0]?.length || 0;
      if (!rows || !cols) return;
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d');
      exportCanvas.width = width * scale;
      exportCanvas.height = height * scale;
      exportCtx.scale(scale, scale);
      // White background
      exportCtx.fillStyle = '#fff';
      exportCtx.fillRect(0, 0, width, height);
      // Cell ratio logic
      const getCellDimensions = (cellRatio) => {
        const ratios = {
          '1:1': { width: 1, height: 1 },
          '3:4': { width: 3, height: 4 },
          '4:3': { width: 4, height: 3 },
          '2:3': { width: 2, height: 3 },
          '3:2': { width: 3, height: 2 }
        };
        return ratios[cellRatio] || ratios['4:3'];
      };
      const cellDimensions = getCellDimensions(cellRatio);
      const cellWidth = width / cols;
      const cellHeight = height / rows;
      const aspectRatio = cellDimensions.width / cellDimensions.height;
      let adjustedCellWidth = cellWidth;
      let adjustedCellHeight = cellHeight;
      if (cellWidth / cellHeight > aspectRatio) {
        adjustedCellWidth = cellHeight * aspectRatio;
      } else {
        adjustedCellHeight = cellWidth / aspectRatio;
      }
      const offsetX = (width - (adjustedCellWidth * cols)) / 2;
      const offsetY = (height - (adjustedCellHeight * rows)) / 2;
      
      // Drawing logic - always using top-down and left-to-right orientation
      for (let visualRow = 0; visualRow < rows; visualRow++) {
        for (let visualCol = 0; visualCol < cols; visualCol++) {
          // Since we always use top-down and left-to-right, visual coordinates = data coordinates
          const colorIndex = colordata[visualRow][visualCol];
          if (colorIndex === null || colorIndex === undefined) continue;
          const color = colors[colorIndex] || colors[0] || '#ffffff';
          const x = offsetX + visualCol * adjustedCellWidth;
          const y = offsetY + visualRow * adjustedCellHeight;
          switch (view) {
            case 'watercolor':
              exportCtx.fillStyle = applyWatercolorEffect(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              break;
            case 'soft':
              exportCtx.fillStyle = applySoftEffect(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              break;
            case 'leftToRightGradient':
              exportCtx.fillStyle = applyLeftToRightGradient(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              break;
            case 'sketch':
              applySketchEffect(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              continue;
            case 'rough':
              drawRoughRect(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              continue;
            case 'watercolorBleed':
              applyWatercolorBleed(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              continue;
            case 'crayon':
              applyCrayonEffect(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              continue;
            case 'ink':
              drawInkOutline(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              continue;
            case 'minimal':
              exportCtx.fillStyle = color;
              break;
            case 'knitstitch':
              drawKnitStitch(exportCtx, x, y, adjustedCellWidth, adjustedCellHeight, color);
              continue;
            default:
              exportCtx.fillStyle = color;
          }
          if (view === 'minimal') {
            exportCtx.fillRect(x, y, adjustedCellWidth, adjustedCellHeight);
          } else {
            const radius = Math.min(adjustedCellWidth, adjustedCellHeight) * 0.1;
            exportCtx.beginPath();
            exportCtx.roundRect(x, y, adjustedCellWidth, adjustedCellHeight, radius);
            exportCtx.fill();
          }
          if (showGrid && view !== 'minimal' && adjustedCellWidth > 3 && adjustedCellHeight > 3) {
            exportCtx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
            exportCtx.lineWidth = 0.5;
            exportCtx.stroke();
          }
        }
      }
      // Draw gridlines if enabled
      if (showGrid) {
        exportCtx.save();
        exportCtx.strokeStyle = 'rgba(0,0,0,0.18)';
        exportCtx.lineWidth = 0.5;
        for (let r = 0; r <= rows; r++) {
          const y = offsetY + r * adjustedCellHeight;
          exportCtx.beginPath();
          exportCtx.moveTo(offsetX, y);
          exportCtx.lineTo(offsetX + cols * adjustedCellWidth, y);
          exportCtx.stroke();
        }
        for (let c = 0; c <= cols; c++) {
          const x = offsetX + c * adjustedCellWidth;
          exportCtx.beginPath();
          exportCtx.moveTo(x, offsetY);
          exportCtx.lineTo(x, offsetY + rows * adjustedCellHeight);
          exportCtx.stroke();
        }
        exportCtx.restore();
      }
      exportCanvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    }
  }), [colordata, colors, width, height, view, cellRatio, showGrid]);

  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: borderRadius / 8,
        overflow: 'hidden',
        border: showBorder ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
        boxShadow: showBorder ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block'
        }}
      />
    </Box>
  );
});

export default ColorThumbnail;
